import { useState, useEffect } from 'react';
import BarChart from './BarChart';
import Title from '../../components/Title';
import styled from 'styled-components';
import Loader from '../../components/Loader';
import Error from '../../components/Error';
import api from '../../app/api';
import Filters from './Filters';
import {
  AGE_GROUP_OPTIONS,
  DOSE_OPTIONS,
  REGION_OPTIONS,
  VACCINE_OPTIONS,
} from '../../app/config';
import GroupedRegions from './GroupedRegions';
import GroupedAgeGroups from './GroupedAgeGroups';
import TotalCount from './TotalCount';
import dayjs from '../../app/date';
import { Emoji } from '../Calculator/CalculatorForm';

const ChartSection = styled.div`
  width: 95vw;
  flex: 1;
  margin: 0 auto;
`;
const ChartWrapper = styled.div`
  min-width: 1000px;
  resize: vertical;
  overflow: hidden;
`;
const OverflowWrapper = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  margin-top: 1em;
`;
const GroupedCharts = styled.div`
  display: flex;
  resize: vertical;
  overflow: hidden;
  margin-top: 1em;
  @media (max-width: 780px) {
    flex-direction: column;
    height: 1000px !important;
  }
`;
const TotalCountWrapper = styled.div`
  overflow: hidden;
  resize: vertical;
  margin-top: 1em;
  display: flex;
  @media (max-width: 780px) {
    height: 250px !important;
  }
`;

const Chart = () => {
  const [datasets, setDatasets] = useState([]);
  const [selectedDatasets, setSelectedDatasets] = useState([]);
  const [selectedDose, setSelectedDose] = useState([DOSE_OPTIONS[0]]);
  const [selectedVaccine, setSelectedVaccine] = useState(VACCINE_OPTIONS);
  const [filters, setFilters] = useState({
    regions: REGION_OPTIONS,
    ageGroups: AGE_GROUP_OPTIONS,
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [groupedRegionsData, setGroupedRegionsData] = useState([]);
  const [groupedAgeGroupsData, setGroupedAgeGroupsData] = useState([]);
  const [origData, setOrigData] = useState([]);
  const [totalCount, setTotalCount] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(false);
      try {
        const { data } = await api.post('chart', {
          datasets: selectedDatasets.map((selected) => selected.value),
          dose: selectedDose.map((selectedDose) => selectedDose.value),
          vaccine: selectedVaccine.map((vaccine) => vaccine.value),
        });

        if (data.error) {
          setError(true);
        }

        let chartData = [];
        const singleDay = Object.keys(data).length === 1;
        for (const date of Object.keys(data)) {
          for (const key of Object.keys(data[date])) {
            const region = data[date][key];
            region.region = singleDay ? key : key + ' ' + date;
            chartData.push(region);
          }
        }

        chartData = chartData
          .sort((a, b) => {
            const first = a.region.split(' ');
            const second = b.region.split(' ');

            const regionCompare =
              first.length === 1
                ? a.region.localeCompare(b.region)
                : first[0].localeCompare(second[0]);
            if (regionCompare === 0 && first.length !== 1) {
              return (
                dayjs(first[1], 'DD.MM.YYYY') - dayjs(second[1], 'DD.MM.YYYY')
              );
            } else {
              return regionCompare;
            }
          })
          .map((group) => {
            const round = { ...group };
            for (const key in round) {
              if (key !== 'region') {
                round[key] = Math.round(round[key]);
              }
            }
            return round;
          });
        setData(chartData);
        setOrigData(data);
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    if (selectedDatasets.length) {
      fetchData();
    }
  }, [selectedDatasets, selectedDose, selectedVaccine]);

  useEffect(() => {
    const fetchDatasets = async () => {
      const { data } = await api.get('datasets');
      const datasetOptions = data.map((dataset) => ({
        label: dataset,
        value: dataset,
      }));
      setDatasets(datasetOptions);
      setSelectedDatasets([datasetOptions[0]]);
    };
    fetchDatasets();
  }, []);

  useEffect(() => {
    let groupedAgeGroups = {};
    let groupedRegions = {};
    let groupedTotal = {};
    const ageGroupsNames = filters.ageGroups.map(
      (region) => 'Vek ' + region.value
    );
    const regionNames = filters.regions.map((region) => region.value);

    const keys = Object.keys(origData);
    for (const date of keys) {
      if (!groupedTotal[date]) {
        groupedTotal[date] = { date, Počet: 0 };
      }

      for (const key of Object.keys(origData[date])) {
        if (!regionNames.includes(key)) {
          continue;
        }

        if (!groupedRegions[key]) {
          groupedRegions[key] = { region: key };
        }
        if (!groupedRegions[key][date]) {
          groupedRegions[key][date] = 0;
        }
        for (const ageGroup of Object.keys(origData[date][key])) {
          if (ageGroup === 'region' || !ageGroupsNames.includes(ageGroup)) {
            continue;
          }

          if (!groupedAgeGroups[ageGroup]) {
            groupedAgeGroups[ageGroup] = { ageGroup };
          }
          if (!groupedAgeGroups[ageGroup][date]) {
            groupedAgeGroups[ageGroup][date] = 0;
          }

          const value = origData[date][key][ageGroup];
          groupedRegions[key][date] += value;
          groupedAgeGroups[ageGroup][date] += value;
          groupedTotal[date]['Počet'] += value;
        }
      }
    }

    setGroupedRegionsData(
      Object.values(groupedRegions).map((group) => {
        const round = { ...group };
        for (const key in round) {
          if (key !== 'region') {
            round[key] = Math.round(round[key]);
          }
        }
        return round;
      })
    );
    setGroupedAgeGroupsData(
      Object.values(groupedAgeGroups).map((group) => {
        const round = { ...group };
        for (const key in round) {
          if (key !== 'ageGroup') {
            round[key] = Math.round(round[key]);
          }
        }
        return round;
      })
    );
    setTotalCount(
      Object.values(groupedTotal)
        .sort(
          (a, b) => dayjs(a.date, 'DD.MM.YYYY') - dayjs(b.date, 'DD.MM.YYYY')
        )
        .map((total) => ({ ...total, Počet: Math.round(total['Počet']) }))
    );
  }, [filters, origData]);

  const realNumOfRegions = selectedDatasets.length * filters.regions.length;

  return (
    <ChartSection>
      <Title>
        Zistite koľko ľudí sa nachádza v čakárni na očkovanie proti Covid-19!{' '}
        <Emoji>🎉</Emoji>
      </Title>
      <Filters
        selectedDatasets={selectedDatasets}
        setSelectedDatasets={setSelectedDatasets}
        datasets={datasets}
        selectedDose={selectedDose}
        setSelectedDose={setSelectedDose}
        filters={filters}
        setFilters={setFilters}
        selectedVaccine={selectedVaccine}
        setSelectedVaccine={setSelectedVaccine}
      />
      {loading && <Loader />}
      <OverflowWrapper>
        {error && <Error />}
        <ChartWrapper
          style={{
            height:
              Math.max(
                realNumOfRegions * (selectedDatasets.length === 1 ? 70 : 60),
                350
              ) + 'px',
          }}
        >
          {Boolean(data.length) && (
            <BarChart
              data={data}
              regions={filters.regions}
              ageGroups={filters.ageGroups}
            />
          )}
        </ChartWrapper>
      </OverflowWrapper>
      {filters.ageGroups.length && filters.regions.length && (
        <>
          <GroupedCharts
            style={{
              height:
                Math.max(
                  data.length * (selectedDatasets.length === 1 ? 60 : 45),
                  350
                ) + 'px',
            }}
          >
            <GroupedRegions
              keys={selectedDatasets.map(
                (selectedDataset) => selectedDataset.value
              )}
              data={groupedRegionsData}
              regions={filters.regions}
            />
            <GroupedAgeGroups
              keys={selectedDatasets.map(
                (selectedDataset) => selectedDataset.value
              )}
              data={groupedAgeGroupsData}
              ageGroups={filters.ageGroups}
            />
          </GroupedCharts>
          <TotalCountWrapper
            style={{
              height: Math.max(selectedDatasets.length * 90, 200) + 'px',
            }}
          >
            <TotalCount
              keys={selectedDatasets.map(
                (selectedDataset) => selectedDataset.value
              )}
              data={totalCount}
            />
          </TotalCountWrapper>
        </>
      )}
    </ChartSection>
  );
};

export default Chart;
